import { create } from 'zustand';

export type UploadedBatchSource = 'import' | 'upload';

/** An upload batch or import whose every file has stopped transferring, reduced to the files that made it. */
export type FinishedUploadBatch = {
  /** The upload batch id, or the import's remote task id. */
  batchId: string;
  attachments: UploadedBatchAttachment[];
  /** When the last file landed, in ms. */
  finishedAt: number;
  source: UploadedBatchSource;
};

/** A batch file shown as a composer scope pill. Not a workspace selection, so the selection bar stays down. */
export type UploadedBatchAttachment = {
  assetId: string;
  id: string;
  /** The file is still processing, so its still is on the way. Uploads never wait: they carry a local preview. */
  isThumbnailPending: boolean;
  name: string;
  thumbnailUrl: string | null;
  versionId: string;
};

export type AttachedUploadBatch = {
  batchId: string;
  fileCount: number;
  source: UploadedBatchSource;
};

export type UploadedBatchOfferState = {
  /** The batch the offer opened a thread on. Drives the empty thread and the composer scope. */
  attachedBatch: AttachedUploadBatch | null;
  attachments: UploadedBatchAttachment[];
  /** Batches whose offer was clicked or dismissed. The offer never returns for them. */
  resolvedBatchIds: ReadonlySet<string>;
  attachBatch: (batch: FinishedUploadBatch) => void;
  clearAttachedBatch: () => void;
  /** Settles stills the attached batch was waiting on. An imported file can still be processing when the thread opens. */
  settleAttachedBatchThumbnails: (batch: FinishedUploadBatch) => void;
  resolveBatch: (batchId: string) => void;
  /** Puts back the batch a failed send cleared. Does not touch `resolvedBatchIds`. */
  restoreAttachedBatch: (snapshot: {
    attachedBatch: AttachedUploadBatch | null;
    attachments: UploadedBatchAttachment[];
  }) => void;
};

const addResolvedBatchId = (resolvedBatchIds: ReadonlySet<string>, batchId: string) =>
  resolvedBatchIds.has(batchId) ? resolvedBatchIds : new Set([...resolvedBatchIds, batchId]);

export const useUploadedBatchOfferStore = create<UploadedBatchOfferState>()((set, get) => ({
  attachedBatch: null,
  attachments: [],
  resolvedBatchIds: new Set(),

  attachBatch: (batch) =>
    set((state) => ({
      attachedBatch: {
        batchId: batch.batchId,
        fileCount: batch.attachments.length,
        source: batch.source,
      },
      attachments: batch.attachments,
      resolvedBatchIds: addResolvedBatchId(state.resolvedBatchIds, batch.batchId),
    })),

  clearAttachedBatch: () => {
    if (get().attachedBatch === null && get().attachments.length === 0) {
      return;
    }

    set({ attachedBatch: null, attachments: [] });
  },

  settleAttachedBatchThumbnails: (batch) =>
    set((state) => {
      if (state.attachedBatch?.batchId !== batch.batchId) {
        return state;
      }

      const incomingById = new Map(batch.attachments.map((attachment) => [attachment.id, attachment]));
      let hasSettled = false;
      const attachments = state.attachments.map((attachment) => {
        const incoming = incomingById.get(attachment.id);

        if (!attachment.isThumbnailPending || !incoming || incoming.isThumbnailPending) {
          return attachment;
        }

        hasSettled = true;

        return { ...attachment, isThumbnailPending: false, thumbnailUrl: incoming.thumbnailUrl };
      });

      return hasSettled ? { attachments } : state;
    }),

  restoreAttachedBatch: (snapshot) => {
    set({
      attachedBatch: snapshot.attachedBatch,
      attachments: snapshot.attachments,
    });
  },

  resolveBatch: (batchId) =>
    set((state) => {
      const resolvedBatchIds = addResolvedBatchId(state.resolvedBatchIds, batchId);

      return resolvedBatchIds === state.resolvedBatchIds ? state : { resolvedBatchIds };
    }),
}));
